// @flow

import * as React from 'react';

import { Link } from 'react-router-dom';

import {
  selectCartUUID,
  type ProductId,
  type ProductPayload,
} from '@gandi/cart';
import { useTranslator } from '@gandi/translate';
import { Base } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';

import { useFeatureFlags } from '../../../../../../hooks/useFeatureFlag';

import { ListRow } from '../../../../ListRow';

import LabelContent from './LabelContent';

export type Props = {
  productId: ProductId,
  owner: $NonMaybeType<ProductPayload['owner']>,
  canChangeOwner: $NonMaybeType<ProductPayload['change_owner']>,
};

export default function OwnerRow({
  productId,
  owner,
  canChangeOwner,
}: Props): React.MixedElement {
  const { FF_CART_OVERVIEW_NDS } = useFeatureFlags();

  const { __ } = useTranslator();
  const cartId = useAppSelector(selectCartUUID);

  const ownerName = `${owner.given} ${owner.family}`;
  const ownerDisplayName = owner.orgname
    ? `${owner.orgname} – ${ownerName}`
    : ownerName;

  if (FF_CART_OVERVIEW_NDS) {
    return (
      <ListRow
        intent="muted"
        title={`${__('Owner')} ${ownerDisplayName}`}
        options={
          !canChangeOwner
            ? [
                __(
                  'The owner of the transferred domains cannot be modified as per registry policy. The owner will remain the same after the transfer.',
                ),
              ]
            : undefined
        }
        optionsArrow={false}
        {...(canChangeOwner && {
          cta: (
            <Link to={`/cart/${cartId}/configure/${productId}/contacts/owner`}>
              {__('Change')}
            </Link>
          ),
        })}
      />
    );
  }

  // Legacy style
  return (
    <>
      <Base
        flex
        flexShrink0
        gap="small"
        marginBottom={canChangeOwner ? 'xsmall' : null}
      >
        <LabelContent label={__('Owner')}>
          {ownerDisplayName}
          {canChangeOwner && (
            <Link to={`/cart/${cartId}/configure/${productId}/contacts/owner`}>
              {__('Change')}
            </Link>
          )}
        </LabelContent>
      </Base>
      {!canChangeOwner && (
        <Base
          marginTop="xxsmall"
          marginBottom="xsmall"
          size="small"
          color="light"
        >
          {__(
            'The owner of the transferred domains cannot be modified as per registry policy. The owner will remain the same after the transfer.',
          )}
        </Base>
      )}
    </>
  );
}
